// src/ExamQuestions6.jsx
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from '../css/modules/ExamQuestions.module.css';

const ExamQuestions6 = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/bron-sportowa-i-amunicja-egzamin-na-pozwolenie');
  };

  return (
    <div className={styles.contactContainer}>
      <Helmet>
        <title>Ucz się na pytaniach egzaminacyjnych na broń – zdobądź patent strzelecki bez zaskoczeń!</title>
        <meta
          name="description"
          content="Poznaj pytania egzaminacyjne dotyczące broni sportowej i amunicji w konkurencjach ISSF. Dowiedz się, czym różni się broń bocznego i centralnego zapłonu oraz jakie wymagania musi spełniać sprzęt używany w zawodach."
        />
      </Helmet>

      <div className={styles.content}>
        <h1 className="mainHeader">Pytania na broń i patent strzelecki – ucz się skutecznie i bez stresu</h1>

        <div className="intro">
          Chcesz podejść do <strong>egzaminu na broń</strong> i zdobyć <strong>patent strzelecki</strong>? Jesteś we właściwym miejscu. W tej części skupiamy się na pytaniach dotyczących broni sportowej oraz amunicji, zwłaszcza w kontekście przepisów ISSF (Międzynarodowej Federacji Strzelectwa Sportowego).

          📘 Prezentujemy zestaw oficjalnych pytań opublikowanych przez Polski Związek Strzelectwa Sportowego (PZSS), wzbogacony o nasze komentarze i doprecyzowania. To sprawdzona i rzetelna pomoc w nauce – zarówno przed samym egzaminem, jak i w dalszej drodze po <strong>pozwolenie na broń</strong>.

          🔗 Jeśli dopiero zaczynasz, sprawdź:
          <ul>
            <li><a href="/egzamin-na-bron">przewodnik: jak wygląda egzamin na broń</a></li>
            <li><a href="/pozwolenie-na-bron-i-ustawa-o-broni-i-amunicji">poradnik o pozwoleniu na broń</a></li>
          </ul>

          🎮 A kiedy przerobisz pytania, koniecznie odwiedź <a href="https://pistolet.org" target="_blank" rel="noopener noreferrer">pistolet.org – testy na patent strzelecki</a>. To nowoczesna forma nauki – quizy, komentarze, system punktacji i baza wiedzy.
        </div>

        <h2>📋 Dane rejestrowe i definicje amunicji</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>84. 🧾 Jakie dane muszą znaleźć się w książce rejestru pobytu na strzelnicy?</h3>
            <p>Imię i nazwisko osoby korzystającej, numer pozwolenia na broń oraz organ wydający pozwolenie. Jeśli osoba nie posiada pozwolenia – podaje adres zamieszkania.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>85. 🔘 Czym jest amunicja bocznego zapłonu?</h3>
            <p>Amunicja, w której zapłon następuje przez uderzenie w obrzeże łuski. Typowa dla kalibru .22 LR – często używana w strzelectwie sportowym i rekreacyjnym.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>86. 🎯 Czym jest amunicja centralnego zapłonu?</h3>
            <p>Amunicja, w której spłonka znajduje się w centralnej części denka łuski. Stosowana w broni bojowej, myśliwskiej i sportowej – zapewnia większą moc i efektywność.</p>
          </li>
        </ul>

        <h2>🏅 Amunicja i broń dopuszczona przez ISSF</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>87–88. 💡 Jaką amunicję dopuszcza ISSF do zawodów?</h3>
            <p>Amunicję bocznego zapłonu o kalibrze 5,6 mm (.22 LR), wykonaną z miękkiego ołowiu. Musi spełniać wymogi dotyczące energii pocisku i przeznaczenia sportowego.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>89. 🔢 Która broń ma największy kaliber wagomiarowy?</h3>
            <p>Broń kal. 12 – ponieważ w przypadku kalibrów wagomiarowych mniejszy numer oznacza większą średnicę przewodu lufy. Kal. 10 będzie większy niż 12, ale 12 to górna granica dopuszczalna w ISSF.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>90. 💨 Jaką broń pneumatyczną dopuszcza ISSF?</h3>
            <h4>Pistolety:</h4>
            <p>Masa do 1500 g, minimalny opór języka spustowego 500 g, maksymalne wymiary 420×200×50 mm.</p>
            <h4>Karabiny:</h4>
            <p>Masa do 5500 g, długość celownika do 850 mm, dopuszczalny dowolny typ mechanizmu spustowego.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>91. 🎯 Jaką broń palną bocznego zapłonu dopuszcza ISSF?</h3>
            <p>Pistolety i rewolwery kal. .22 LR, karabinki do 762 mm długości lufy i 8000 g masy. Stosowana w konkurencjach karabinowych i pistoletowych dla juniorów i dorosłych.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>92. 🟡 Czy broń śrutowa może być używana w ISSF?</h3>
            <p>Tak. Dozwolona jest broń gładkolufowa kal. 12 lub mniejszym (czyli numer większy), z komorą nabojową do 76 mm. Wykorzystywana w trapie i skeecie.</p>
          </li>

          <li className={styles.customAnswer}>
            <h3>93. 🔫 Jaką broń centralnego zapłonu dopuszcza ISSF?</h3>
            <h4>Pistolety i rewolwery:</h4>
            <p>Kalibry od 7,62 mm (.30") do 9,65 mm (.38"). Maksymalna masa: 1400 g. Lufa: max 153 mm długości.</p>
            <h4>Karabiny:</h4>
            <p>Kaliber do 8 mm (.315"), maksymalna masa: 8000 g.</p>
          </li>
        </ul>

        <h2>🚫 Amunicja niedozwolona przez ISSF</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>94. ❌ Jakiej amunicji nie wolno stosować w zawodach ISSF?</h3>
            <p>Amunicji zawierającej stalowe rdzenie, pociski przeciwpancerne, zapalające lub wybuchowe. Niedozwolona jest także amunicja przekraczająca limity energii określone w regulaminach.</p>
          </li>
        </ul>

        <div className="buttonContainer">
          <button onClick={handleNext} className={styles.secondaryButton}>
            Kolejne pytania z odp
          </button>
          <button onClick={() => navigate('/')} className={styles.primaryButton}>
            Testy egzaminacyjne – patent strzelecki
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamQuestions6;
